/**
 * Сдвиг времён строки квеста в сетке ДК (вынесено из useGridData.ts 04.10 без смены поведения
 * сдвига назад и МК). Три механизма:
 *  - чайная назад (V1): игра шла по сетке с чайной, а чайную не взяли — свободные слоты до
 *    следующей брони показываются на 45 минут раньше (филиал shifting);
 *  - мастер-класс (17.09, mc_shift): следующий базовый слот после игры с МК — на минуты МК позже;
 *  - лишняя чайная вперёд (04.10, владелец): чайная взята там, где по умолчанию её нет (после
 *    отсечки при tea_after=0, ночью) — квест занят ещё 45 минут, и свободные слоты уезжают вперёд
 *    цепочкой до следующей настоящей брони: Психушка 03.10, 19:00 с чайной → 20:30 станет 21:15,
 *    22:00 → 22:45, 23:30 → 00:15, 01:00 → 01:45. Занятые ячейки не двигаются никогда: игра,
 *    стоящая раньше, чем квест освободится, получает метку «чайная сдвигает — перенесите».
 * Правило «положена ли чайная» и лишние минуты — общие с гейтом и публичными слотами (@ub/shared/teaSpan).
 */
import { baseSlotOf, dayOrd, expectTea, honorsMcShift, minToHhmm, shiftedTime, teaExtraMinutes, type TeaRule } from "@ub/shared";
import type { Booking, BookingsMap } from "./bookingTypes";
import type { VersionSpan } from "./useGridData";
import { TEA_MIN_DEFAULT, type BranchConfig } from "./branchConfig";
import { blockReasonOf } from "../components/modal/blockInfo";

export interface ShiftedSlot {
  base: string;
  disp: string;
  /** Сдвиг чайной (назад), минуты. */
  shift: number;
  /** 17.09: сдвиг мастер-классом (вперёд), минуты. */
  mcShift: number;
  /** 04.10: сдвиг лишней чайной (вперёд, цепочкой), минуты. */
  teaShift?: number;
  /** Подсказка с меткой ячейки: игру сдвигает МК или чайная (куда её перенести) либо блок «Чайная» стал лишним. */
  clash?: string;
  /** 04.10 (ревью): бронь, чей МК или лишняя чайная сдвинули эту свободную ячейку, — в режиме переноса
   *  самой этой брони сдвиг не в счёт (иначе игра «переезжает» на время, которое создаёт она же). */
  srcId?: number;
  /** 04.10 (ревью): сдвиг съел ячейку целиком (МК + чайная ≥ шага) — сдвинутое время уже следующий слот. */
  consumed?: boolean;
}

/** 04.10: какую нестандартную игру remapCustomBookings оставляет в левой колонке (baseSlotOf): в филиале
 *  со сдвигом МК — любую; со сдвигом чайной — не дальше 45 минут от колонки (21:15 в колонке 20:30). */
export function keepInLeftColumn(cfg: Pick<BranchConfig, "mcShift" | "shifting"> | undefined, t: string, col: string): boolean {
  return !!cfg?.mcShift || (!!cfg?.shifting && dayOrd(t) - dayOrd(col) <= TEA_MIN_DEFAULT);
}

/** 04.10 (ревью): время, на которое ставить бронь по клику на свободную ячейку. Только сдвиг ВПЕРЁД (МК,
 *  лишняя чайная): при сдвиге назад бронь, как и раньше, ложится на базовое время — иначе 19:45 уехала бы
 *  в левую колонку 19:00 и оставила свою пустой. В переносе брони, которая сама даёт сдвиг, — базовое. */
export function presetTimeOf(slot: ShiftedSlot, movingId: number | null): string | null {
  const fwd = slot.mcShift > 0 || (slot.teaShift ?? 0) > 0;
  if (!fwd || slot.disp === slot.base || slot.consumed) return null;
  if (movingId != null && slot.srcId === movingId) return null;
  return slot.disp;
}

/** Текст метки занятой ячейки — в title ячейки и плашке модалки брони. */
const clashText = (by: string, at: string) => `${by} перед этой игрой сдвигает её на ${at} — перенесите или предупредите клиента`;

export function computeShiftedSlots(
  questId: string,
  baseSlots: string[],
  bookings: BookingsMap,
  cfg: BranchConfig,
  mcNext: Map<string, number> = new Map(),
  /** 04.10: игра + уборка квеста — свободная ячейка, проданная по сдвинутому времени, тянет цепочку дальше. */
  span = 0,
  /** 04.10: занятость квеста бронью (игра + уборка версии + МК в филиале с mc_shift) — occupancyOf. */
  occOf: (b: Booking) => number = () => span,
): ShiftedSlot[] {
  /* Ни сдвига чайной, ни МК — строка как есть. */
  if (!cfg.shifting && mcNext.size === 0) {
    return baseSlots.map((t) => ({ base: t, disp: t, shift: 0, mcShift: 0 }));
  }

  const teaMin = cfg.teaMin ?? TEA_MIN_DEFAULT;
  const rule: TeaRule = { shifting: !!cfg.shifting, cutoff: cfg.teaCutoff ?? "19:00", before: !!cfg.teaBefore, after: !!cfg.teaAfter };
  const result: ShiftedSlot[] = [];
  let shiftAccum = 0;
  /* 10.09 (оператор: «кажется всё сломала»): сетка обязана идти по возрастанию. Сдвиг за
     неоплаченное чаепитие тянет слот назад, и без ограничения он заезжал раньше предыдущей
     строки — в списке появлялось 18:30, а следом 18:15. Держим нижнюю границу.
     Сутки считаем от открытия: ночные слоты идут после вечерних, а не перед утренними. */
  const dayStart = 6 * 60;
  const ord = (t: string) => { const [h, m] = t.split(":").map(Number); const v = (h ?? 0) * 60 + (m ?? 0); return v < dayStart ? v + 24 * 60 : v; };
  let floor = -1;
  /* 04.10: цепочка лишней чайной (минуты по порядку смены). fwd — когда квест освободится, если
     свободные ячейки продавать по сдвинутому времени; real — когда он освободится после настоящих
     броней (от него метка столкновения: свободная ячейка — ещё не игра, ей уступать некому). */
  let fwd: number | null = null;
  let real: number | null = null;
  let prevTea = false;   // предыдущая ячейка — игра или лид с чайной
  let lastId: number | undefined;    // последняя своя бронь строки с МК — источник сдвига МК следующего слота (МК связанного квеста — не она)
  let chainId: number | undefined;   // бронь, начавшая цепочку лишней чайной

  for (let i = 0; i < baseSlots.length; i++) {
    const base = baseSlots[i] as string;
    const key = `${questId}_${base}`;
    const b = bookings[key];

    /* 17.09: МК игры перед этим слотом сдвигает его вперёд; ручной блок ДК стоит на своём времени */
    const mc = b?.type === "block" ? 0 : (mcNext.get(base) ?? 0);

    if (b && (b.type === "booking" || b.type === "lead")) {
      const booking = b as Booking;
      const shownAt = booking.customTime || base;   // нестандартная игра показывает своё время
      /* Игра стоит на сдвинутом слоте раньше, чем МК его освободит (поставлена до МК или
         продавлена force) — помечаем, куда её перенести. Сдвинутая (17:45) — на месте. */
      const actual = booking.customTime || (booking.time !== base ? booking.time : null);
      /* пересаженная нестандартная игра (16:30 в колонке 17:00) стоит не на этом слоте — гейт её не блокирует, бейдж не нужен */
      const onThisSlot = !actual || baseSlotOf(baseSlots, actual) === base;
      const mcClash = mc > 0 && onThisSlot && !honorsMcShift(base, actual, mc) ? shiftedTime(base, mc) : undefined;
      /* 04.10: игра начинается раньше, чем квест освободится после игры с лишней чайной */
      const start = ord(actual || base);
      const teaClash = !mcClash && real !== null && start < real ? minToHhmm(real) : undefined;
      const clash = mcClash ? clashText("Мастер-класс", mcClash) : teaClash ? clashText("Чайная", teaClash) : undefined;
      result.push({ base, disp: base, shift: 0, mcShift: 0, ...(clash ? { clash } : {}) });
      floor = Math.max(floor, ord(shownAt));
      shiftAccum = cfg.shifting && expectTea(rule, base) && !booking.tea ? teaMin : 0;
      /* 04.10: лишняя чайная начинает цепочку; игра на сдвинутом месте (21:15 в колонке 20:30) её продолжает,
         игра на своём базовом времени — обрывает. */
      const extra = teaExtraMinutes(rule, actual || base, booking.tea);
      if (extra > 0) chainId = booking.id; else if (!(fwd !== null && start > ord(base))) chainId = undefined;
      fwd = extra > 0 || (fwd !== null && start > ord(base)) ? start + occOf(booking) + extra : null;
      lastId = booking.mc && booking.mc !== "none" ? booking.id : undefined;
      real = fwd;
      prevTea = !!booking.tea;
    } else {
      const [h, m] = base.split(":").map(Number);
      /* 04.10: блок и автоблок стоят на своём времени и цепочку лишней чайной гасят — квест в их время не играет. */
      const held = b?.type === "block" || b?.type === "auto-block";
      const teaFwd: number = !held && fwd !== null && fwd > ord(base) ? fwd - ord(base) : 0;
      /* Слот после игры с МК = base + МК ровно (как считают гейт API и публичные слоты);
         чайный сдвиг назад к нему не применяется — иначе МК 45 и «чайной не было» (−45)
         гасили бы друг друга, и ДК видел бы 17:00, которое гейт не даст продать. */
      let mins = mc > 0 || teaFwd > 0 ? (h ?? 0) * 60 + (m ?? 0) + Math.max(mc, teaFwd) : (h ?? 0) * 60 + (m ?? 0) - shiftAccum;
      if (mins < 0) mins += 24 * 60;
      let dh = String(Math.floor(mins / 60) % 24).padStart(2, "0");
      let dm = String(mins % 60).padStart(2, "0");
      if (ord(`${dh}:${dm}`) < floor) {           // сдвиг заехал бы раньше предыдущей строки
        const keep = Math.min(ord(base), floor) % (24 * 60);
        dh = String(Math.floor(keep / 60) % 24).padStart(2, "0");
        dm = String(keep % 60).padStart(2, "0");
      }
      const next = baseSlots[i + 1];
      /* 04.10 (ревью): МК + чайная дотянули ячейку до следующего слота — продавать в ней нечего */
      const consumed = !held && (mc > 0 || teaFwd > 0) && !!next && ord(`${dh}:${dm}`) >= ord(next);
      const clash = b?.type === "block" && cfg.shifting ? teaBlockNote(b as Booking, fwd !== null && fwd > ord(base) ? minToHhmm(fwd) : null, prevTea)
        : consumed ? `Сдвиг мастер-класса и чайной занял этот слот целиком — ближайшее время ${next}` : undefined;
      const srcId = teaFwd > 0 ? chainId : mc > 0 ? lastId : undefined;
      result.push({ base, disp: `${dh}:${dm}`, shift: shiftAccum, mcShift: mc, ...(teaFwd ? { teaShift: teaFwd } : {}), ...(clash ? { clash } : {}), ...(srcId !== undefined ? { srcId } : {}), ...(consumed ? { consumed } : {}) });
      floor = Math.max(floor, ord(`${dh}:${dm}`));
      /* сдвинутая ячейка, проданная по своему времени, тянет цепочку дальше; съеденная — не продаётся, квест свободен с fwd (ревью 04.10) */
      if (!consumed) fwd = teaFwd > 0 ? ord(`${dh}:${dm}`) + span : null;
      prevTea = false;
    }
  }
  return result;
}

/** 04.10: занятость квеста бронью — игра + уборка (спецверсии — её, как eff() гейта) + МК, если филиал
 *  со сдвигом мастер-классом (гейт считает МК частью игры только там). */
export function occupancyOf(q: { duration: number; reset_time: number }, versions: readonly VersionSpan[], mkOf?: (b: Booking) => number) {
  const sv = new Map(versions.map((v) => [v.id, v]));
  return (b: Booking): number => {
    const v = b.specialVersionId ? sv.get(b.specialVersionId) : undefined;
    return Number(v?.duration_min ?? q.duration ?? 0) + Number(v?.reset_min ?? q.reset_time ?? 0) + (mkOf ? mkOf(b) : 0);
  };
}

/**
 * 04.10 (владелец): ручные блоки «Чайная» (ДК закрывал ими хвост лишней чайной) не удаляем, а
 * показываем, что они больше не нужны: блок по-прежнему закрывает свою ячейку, но цепочку
 * сдвига теперь ведёт сама сетка. Метка — в title ячейки и плашке модалки блока.
 */
function teaBlockNote(block: Booking, opensAt: string | null, prevTea: boolean): string | undefined {
  const reason = blockReasonOf(block);
  if (!/чайн/i.test(reason)) return undefined;
  if (opensAt) return `Блок «${reason}»: чайная игры перед ним теперь сама сдвигает сетку — блок можно снять, слот откроется на ${opensAt}`;
  if (!prevTea) return `Блок «${reason}»: перед ним нет игры с чайной — проверьте, нужен ли он`;
  return undefined;
}
